import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Search, Save, RefreshCw, Check, UserCheck, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';
import * as validationService from '@/services/validationService';

const SeasonalAvailablePickers = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [nameById, setNameById] = useState({});
  const [selected, setSelected] = useState(() => new Set());
  const [current, setCurrent] = useState([]);
  const [canManage, setCanManage] = useState(true);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  const mergeNames = useCallback((list) => {
    setNameById((prev) => {
      const next = { ...prev };
      (list || []).forEach((s) => { next[s.staff_id] = s.staff_name || s.name || s.staff_id; });
      return next;
    });
  }, []);

  const runSearch = useCallback(async (term) => {
    setSearching(true);
    try { const res = await validationService.getStaffList(term || null, null, 100); setStaff(res.staff || []); mergeNames(res.staff || []); }
    catch { toast.error('Failed to search staff'); }
    finally { setSearching(false); }
  }, [mergeNames]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [staffRes, availRes] = await Promise.all([
        validationService.getStaffList(null, null, 100),
        api.getPickers(),
      ]);
      setStaff(staffRes.staff || []); mergeNames(staffRes.staff || []);
      const pickers = availRes.pickers || [];
      setCurrent(pickers); mergeNames(pickers);
      setCanManage(availRes.can_manage !== false);
      setSelected(new Set(pickers.map((p) => p.staff_id)));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load pickers'); }
    finally { setLoading(false); }
  }, [mergeNames]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(search.trim()), 350);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search, runSearch]);

  const toggle = (sid) => { if (!canManage) return; setSelected((p) => { const n = new Set(p); n.has(sid) ? n.delete(sid) : n.add(sid); return n; }); };
  const selectedList = useMemo(() => Array.from(selected).map((sid) => ({ staff_id: sid, name: nameById[sid] || sid })), [selected, nameById]);

  const save = async () => {
    setSaving(true);
    try { const res = await api.setPickers(Array.from(selected)); toast.success(res.message || 'Saved'); setSearch(''); await fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-available-pickers-page">
      <div className="bg-[#1A1F2E] text-[#F9FAFB] px-4 h-16 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/wms/seasonal-bundle')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="sap-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Users className="w-6 h-6 text-teal-400" />
        <div className="flex-1">
          <h1 className="text-lg font-bold">Available Pickers (Today)</h1>
          <p className="text-xs text-[#E8DCC8]">New Seasonal pick slips are auto-assigned to these pickers, round-robin</p>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchAll} className="text-[#F9FAFB] hover:bg-white/10" data-testid="sap-refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <div className="max-w-5xl mx-auto p-4 grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Search className="w-4 h-4" /> Select Pickers</CardTitle></CardHeader>
          <CardContent>
            {!canManage && <p className="text-xs text-amber-600 mb-2" data-testid="sap-readonly-note">View only — a Supervisor/Incharge can edit the picker pool.</p>}
            {canManage && selectedList.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2" data-testid="sap-selected-chips">
                {(selectedList || []).map((s) => (
                  <span key={s.staff_id} className="inline-flex items-center gap-1 text-xs bg-teal-100 text-teal-800 rounded-full pl-2 pr-1 py-0.5">
                    {s.name}
                    <button type="button" onClick={() => toggle(s.staff_id)} className="hover:bg-teal-200 rounded-full p-0.5" data-testid={`sap-chip-remove-${s.staff_id}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="relative mb-2">
              <Input placeholder="Search staff by name or ID…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="sap-staff-search" />
              {searching && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-2.5 text-gray-400" />}
            </div>
            <div className="space-y-1 max-h-[420px] overflow-y-auto">
              {loading ? <p className="text-sm text-gray-400 py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>
                : staff.length === 0 ? <p className="text-sm text-gray-400 py-6 text-center" data-testid="sap-no-staff">No staff found</p>
                  : staff.map((st) => {
                    const sid = st.staff_id; const on = selected.has(sid);
                    return (
                      <button key={sid} type="button" onClick={() => toggle(sid)} disabled={!canManage}
                        className={`w-full flex items-center justify-between gap-2 rounded px-3 py-2 border text-left transition-colors ${on ? 'bg-teal-50 border-teal-400' : 'bg-white border-gray-200 hover:border-gray-300'} ${!canManage ? 'opacity-70 cursor-default' : ''}`}
                        data-testid={`sap-staff-${sid}`}>
                        <div className="min-w-0"><p className="text-sm font-medium truncate">{st.staff_name || st.name || sid}</p><p className="text-xs text-gray-500">{sid}</p></div>
                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${on ? 'bg-teal-600 border-teal-600 text-white' : 'border-gray-300'}`}>{on && <Check className="w-3.5 h-3.5" />}</span>
                      </button>
                    );
                  })}
            </div>
            {canManage && (
              <Button className="w-full mt-3 bg-teal-600 hover:bg-teal-700" disabled={saving} onClick={save} data-testid="sap-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save {selected.size} Picker{selected.size !== 1 ? 's' : ''} for Today
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><UserCheck className="w-4 h-4" /> Today's Pickers ({current.length})</CardTitle></CardHeader>
          <CardContent>
            {current.length === 0 ? <p className="text-sm text-gray-400 py-6 text-center" data-testid="sap-no-current">No pickers set for today yet</p>
              : (
                <div className="space-y-2 max-h-[480px] overflow-y-auto" data-testid="sap-current-list">
                  {current.map((p) => (
                    <div key={p.staff_id} className="flex items-center justify-between gap-2 bg-gray-50 rounded px-3 py-2 border" data-testid={`sap-current-${p.staff_id}`}>
                      <div className="min-w-0"><p className="text-sm font-medium truncate">{p.staff_name || p.staff_id}</p><p className="text-xs text-gray-500">{p.staff_id}</p></div>
                      <div className="flex gap-1 shrink-0">
                        <Badge className="bg-orange-100 text-orange-700 border-0">{p.active_slips || 0} active</Badge>
                        <Badge className="bg-green-100 text-green-700 border-0">{p.completed_today || 0} done</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SeasonalAvailablePickers;
